import { useEffect, useRef, type ComponentType, type JSX, type SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuthStore } from '../../state/auth.store';
import { useRevealGate } from './anim';
import { CreateRoomButton } from './CreateRoomButton';
import { LandingScene } from './LandingScene';
import { IconMap, IconVoice, IconFace, IconScreen } from './icons';

/**
 * Shared "Secondary" action styling — a calm ghost button that never competes with the amber primary CTA.
 * Worn by the hero's auth-conditional secondary action: the "Map editor" route link (returning account) or
 * the "Sign in" link (logged-out), so whichever shows reads as the same calm family beside the amber CTA.
 */
const SECONDARY_ACTION =
  'inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-4 text-base font-semibold text-white/85 outline-none transition hover:border-white/35 hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-amber-300/70';

/** Three honest reassurance points, as tidy check-chips under the CTA. NB: no "no account needed" — the
 *  product is behind a login wall now, so that would be false; these three stay true (free to start, fast,
 *  browser-only). */
const ASSURANCES = ['Free to start', 'Up in seconds', 'Works in your browser'] as const;

/**
 * The hero. Left: the headline + subhead + the primary CTA (Sign up / Enter lobby) and a ghost secondary
 * (Sign in / Map editor). Right: the living {@link LandingScene} vignette (the real product world), now
 * framed with floating STATUS pills that telegraph the signature mechanics (room voice / on camera / screen
 * share) happening in the world at a glance, plus a cursor-follow spotlight that makes the hero feel premium.
 *
 * The text is the critical content and renders immediately; the Pixi scene is lazy-loaded inside its host
 * (off the critical path). The scene's box is reserved up front so there's no layout shift when Pixi paints.
 *
 * RESPONSIVE: the two columns now split at `md` (was `lg`) so the living scene stays ABOVE the fold on
 * tablets/iPad instead of being pushed under the copy. Motion is gated: reduced motion / SSG / boot-over-
 * prerender render the text fully visible with no entrance animation, and the ambient glow + pill bob + the
 * cursor spotlight all fall back to a calm static state.
 */
export function HeroSection(): JSX.Element {
  // Two distinct gates: `reveal` silences the ENTRANCE rise (device reduced-motion, SSG prerender, or first
  // mount over prerendered markup); `reduce` is the device preference alone and gates the AMBIENT motion.
  const reveal = useRevealGate();
  const reduce = useReducedMotion();
  // The secondary hero action is auth-aware: a logged-OUT visitor gets "Sign in" (→ /login), pairing with
  // the primary "Sign up" CTA; a RETURNING authed device keeps the "Map editor" creator link.
  const authed = useAuthStore((s) => s.tokens !== null);
  const heroRef = useRef<HTMLElement>(null);
  const spotRef = useRef<HTMLDivElement>(null);

  // Cursor-follow spotlight: a soft warm light tracks the pointer across the hero. Writes CSS vars directly
  // (no React re-render), rAF-throttled, and only on a FINE pointer with motion allowed — touch + reduced
  // motion keep the calm default glow. Cheap: a single compositor-friendly radial gradient.
  useEffect(() => {
    const hero = heroRef.current;
    const spot = spotRef.current;
    if (!hero || !spot) return;
    const fine = window.matchMedia?.('(pointer: fine)').matches;
    if (!fine || reduce) return;
    let raf = 0;
    let x = 0;
    let y = 0;
    const onMove = (e: PointerEvent): void => {
      const r = hero.getBoundingClientRect();
      x = e.clientX - r.left;
      y = e.clientY - r.top;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          raf = 0;
          spot.style.setProperty('--mx', `${x}px`);
          spot.style.setProperty('--my', `${y}px`);
        });
      }
    };
    hero.addEventListener('pointermove', onMove);
    return () => {
      hero.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduce]);

  // Entrance: a soft staggered rise for the headline block. Collapsed to "already visible" under reveal.
  const rise = reveal
    ? {}
    : {
        initial: { opacity: 0, y: 24, filter: 'blur(8px)' },
        animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
      };

  return (
    <section
      ref={heroRef}
      id="top"
      aria-labelledby="hero-heading"
      className="relative flex min-h-[100svh] items-center overflow-hidden pt-16"
    >
      {/* Soft animated light behind the whole hero (GPU-friendly: a slowly drifting radial glow). */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute -top-1/3 left-1/2 h-[80vh] w-[80vh] -translate-x-1/2 rounded-full opacity-60 blur-3xl ${reduce ? '' : 'animate-pulse'}`}
          style={{ background: 'radial-gradient(circle, rgba(255,210,63,0.18), transparent 60%)' }}
        />
      </div>

      {/* Cursor-follow spotlight (defaults to a warm glow over the scene side; follows the pointer on desktop). */}
      <div
        ref={spotRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(600px circle at var(--mx, 72%) var(--my, 32%), rgba(255,210,63,0.08), transparent 45%)',
        }}
      />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-10 px-4 sm:px-6 md:grid-cols-2 md:gap-8 lg:px-8">
        {/* ── Copy ── */}
        <motion.div
          {...rise}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 text-center md:text-left"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-xs font-semibold text-amber-200">
            <span aria-hidden="true">✨</span> The world&apos;s first Social Workspace MMORPG
          </span>

          <h1
            id="hero-heading"
            className="mt-5 text-balance text-5xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl md:text-5xl lg:text-7xl"
          >
            Your space,{' '}
            <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-300 bg-clip-text text-transparent">
              alive.
            </span>
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-pretty text-lg text-white/70 sm:text-xl md:mx-0">
            Build your own cozy virtual space — a team office, an online café, a cinema, a game night —
            where people walk up to talk, share screens, show their faces, and genuinely enjoy being together.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row md:justify-start">
            <CreateRoomButton size="lg" pulse />
            {authed ? (
              /* Map editor — the level-authoring tool, sitting beside the primary CTA. It's dev/admin-gated
                 by default, so we link with the supported `?editor=1` flag (opens + persists it) rather than
                 a bare /editor that would bounce a visitor to the lobby. Only shown to a returning account —
                 a logged-out visitor sees "Sign in" here and reaches the editor from the lobby once in. */
              <Link to="/editor?editor=1" data-testid="cta-map-editor" className={SECONDARY_ACTION}>
                <IconMap className="h-5 w-5" /> Map editor
              </Link>
            ) : (
              /* Logged-out: the secondary action is "Sign in" for returning people who already have an
                 account, pairing with the primary "Sign up". */
              <Link to="/login" data-testid="cta-sign-in" className={SECONDARY_ACTION}>
                Sign in
              </Link>
            )}
          </div>

          {/* Reassurances as tidy check-chips — reads more premium + scannable than a grey dot-joined line. */}
          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 md:justify-start">
            {ASSURANCES.map((a) => (
              <li key={a} className="flex items-center gap-1.5 text-sm text-white/65">
                <span aria-hidden="true" className="text-amber-300">✓</span>
                {a}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* ── Living scene ── reserved box (aspect-ratio), Pixi lazy-loads on top, CSS fallback fills it. */}
        <motion.div
          initial={reveal ? false : { opacity: 0, scale: 0.96 }}
          animate={reveal ? undefined : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-0 mx-auto w-full max-w-xl"
        >
          {/* aspect-[4/3] reserves the box up front so Pixi mounting never shifts layout. */}
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/5">
            <LandingScene />
            {/* A soft inner vignette so the scene melts into the card edges (depth, premium feel). */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-3xl"
              style={{ boxShadow: 'inset 0 0 80px 10px rgba(8,12,28,0.55)' }}
            />
          </div>

          {/* Floating STATUS pills — the signature mechanics, labelled live off the world (desktop/tablet only
              so they never overflow the full-width mobile scene). Gentle bob, silenced under reduced motion. */}
          <ScenePill icon={IconVoice} label="Room voice" reduce={!!reduce} delay={0} className="-left-3 top-8 hidden sm:flex" />
          <ScenePill icon={IconFace} label="On camera" reduce={!!reduce} delay={1.1} className="-right-3 top-1/2 hidden sm:flex" />
          <ScenePill icon={IconScreen} label="Sharing a screen" reduce={!!reduce} delay={2.1} className="bottom-10 -left-4 hidden sm:flex" />

          {/* A floating "live" pill — tiny detail that says "real people, right now". */}
          <div className="pointer-events-none absolute -bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-[#0b1020]/80 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur-md">
            {/* A tiny cluster of coloured "presence" dots so it feels populated, not a lone indicator. */}
            <span aria-hidden="true" className="flex -space-x-1">
              <span className="h-2.5 w-2.5 rounded-full border border-[#0b1020] bg-amber-300" />
              <span className="h-2.5 w-2.5 rounded-full border border-[#0b1020] bg-emerald-400" />
              <span className="h-2.5 w-2.5 rounded-full border border-[#0b1020] bg-sky-400" />
            </span>
            Live, together
          </div>
        </motion.div>
      </div>

      {/* Scroll cue — a soft bouncing chevron nudging the visitor into the page (hidden on short viewports). */}
      <a
        href="#features"
        aria-label="Scroll to explore"
        className="group absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-white/40 outline-none transition hover:text-white/70 focus-visible:ring-2 focus-visible:ring-amber-300/70 sm:flex"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.25em]">Explore</span>
        <svg viewBox="0 0 24 24" className={`h-5 w-5 ${reduce ? '' : 'ddo-scroll-cue'}`} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </a>
    </section>
  );
}

/**
 * A small floating status pill anchored to the hero scene — a branded icon + label that reads as a live
 * status flag popping off the world ("Room voice", "On camera"…). Gentle vertical bob, silenced under
 * reduced motion. Decorative → aria-hidden (the features grid carries the real, labelled meaning).
 */
function ScenePill({
  icon: Icon,
  label,
  className,
  delay,
  reduce,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  className?: string;
  delay: number;
  reduce: boolean;
}): JSX.Element {
  return (
    <motion.div
      aria-hidden="true"
      className={`absolute z-20 flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0b1020]/85 px-2.5 py-1.5 text-xs font-semibold text-white/85 shadow-[0_8px_24px_rgba(0,0,0,0.4)] backdrop-blur-md ${className ?? ''}`}
      initial={false}
      animate={reduce ? undefined : { y: [0, -6, 0] }}
      transition={reduce ? undefined : { duration: 4, repeat: Infinity, ease: 'easeInOut', delay }}
    >
      <Icon className="h-3.5 w-3.5 text-amber-300" />
      {label}
    </motion.div>
  );
}
